import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar } from "lucide-react";

const Attendance = ({ user }) => {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedSession, setSelectedSession] = useState(getDefaultSession());
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingStatus, setSavingStatus] = useState({}); // Track individual button saves

  function getDefaultSession() {
    const hour = new Date().getHours();
    return hour < 13 ? "morning" : "afternoon";
  }

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchStudentsAndAttendance();
    }
  }, [selectedClass, selectedDate, selectedSession]);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      const allClasses = response.data;
      
      // Backend already filters classes based on user role - no additional filtering needed
      setClasses(allClasses);
      if (allClasses.length > 0) {
        setSelectedClass(allClasses[0].id);
      }
    } catch (error) {
      toast.error("Failed to load classes");
    }
  };

  const fetchStudentsAndAttendance = async () => {
    setLoading(true);
    try {
      const [studentsRes, attendanceRes] = await Promise.all([
        axios.get(`${API}/students`),
        axios.get(
          `${API}/attendance?class_id=${selectedClass}&date=${selectedDate}`
        ),
      ]);

      const classStudents = studentsRes.data.filter(
        (s) => s.class_id === selectedClass
      );
      setStudents(classStudents);

      // Build attendance records map
      const records = {};
      attendanceRes.data.forEach((att) => {
        if (att.session === selectedSession) {
          records[att.student_id] = att.status;
        }
      });
      setAttendanceRecords(records);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const updateAttendance = async (studentId, status) => {
    // Immediate local update for responsive UI
    setAttendanceRecords({
      ...attendanceRecords,
      [studentId]: status,
    });

    // Mark this student as saving
    setSavingStatus({
      ...savingStatus,
      [studentId]: true
    });

    try {
      // Send immediate API call to update attendance
      await axios.post(`${API}/attendance/update`, {
        student_id: studentId,
        class_id: selectedClass,
        date: selectedDate,
        session: selectedSession,
        status: status
      });
      
      toast.success(`Status updated: ${status}`);
    } catch (error) {
      // Revert local state on error
      setAttendanceRecords(prev => ({
        ...prev,
        [studentId]: prev[studentId] || "absent"
      }));
      toast.error("Update failed - please try again");
    } finally {
      // Clear saving state
      setSavingStatus(prev => ({
        ...prev,
        [studentId]: false
      }));
    }
  };

  const markAllPresent = () => {
    const allPresent = {};
    students.forEach((student) => {
      allPresent[student.id] = "present";
    });
    setAttendanceRecords(allPresent);
  };

  const submitAttendance = async () => {
    const records = students.map((student) => ({
      student_id: student.id,
      status: attendanceRecords[student.id] || "absent",
    }));

    try {
      await axios.post(`${API}/attendance/bulk`, {
        class_id: selectedClass,
        date: selectedDate,
        session: selectedSession,
        records,
      });
      toast.success("Attendance saved successfully");
      fetchStudentsAndAttendance();
    } catch (error) {
      toast.error("Failed to save attendance");
    }
  };

  const selectedClassName = classes.find((c) => c.id === selectedClass)?.name;

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div>
        <h2 className="text-2xl font-bold text-[#1E624A]">Mark Attendance</h2>
        <p className="text-sm text-gray-600">Qor Soo-gooshashada</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl p-6 shadow-md space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Class
            </label>
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger data-testid="attendance-class-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {classes.map((cls) => (
                  <SelectItem key={cls.id} value={cls.id}>
                    {cls.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date
            </label>
            <div className="relative">
              <Calendar
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="date"
                data-testid="attendance-date-input"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#1E624A]"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Session
            </label>
            <Select value={selectedSession} onValueChange={setSelectedSession}>
              <SelectTrigger data-testid="attendance-session-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="morning">Morning</SelectItem>
                <SelectItem value="afternoon">Afternoon</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            data-testid="mark-all-present-btn"
            onClick={markAllPresent}
            variant="outline"
            className="border-[#1E624A] text-[#1E624A] hover:bg-[#1E624A] hover:text-white"
          >
            Mark All Present
          </Button>
        </div>
      </div>

      {/* Attendance table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : students.length > 0 ? (
        <div className="bg-white rounded-xl shadow-md overflow-hidden attendance-table-clean">
          <div className="p-4 bg-[#1E624A] text-white">
            <h3 className="font-semibold">
              {selectedClassName} - {selectedDate} ({selectedSession})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => {
                  const currentStatus = attendanceRecords[student.id];
                  const isUpdating = savingStatus[student.id];
                  
                  return (
                    <tr key={student.id} data-testid={`attendance-row-${student.id}`}>
                      <td className="font-medium">{student.name}</td>
                      <td>
                        <div className="flex gap-2">
                          {["present", "absent", "late", "excused"].map((status) => (
                            <button
                              key={status}
                              data-testid={`attendance-${status}-${student.id}-btn`}
                              onClick={() => updateAttendance(student.id, status)}
                              disabled={isUpdating}
                              className={`attendance-button ${
                                currentStatus === status
                                  ? `active-${status}`
                                  : "inactive"
                              }`}
                            >
                              {isUpdating && currentStatus === status ? (
                                <span className="flex items-center gap-1">
                                  <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
                                  {status.charAt(0).toUpperCase() + status.slice(1)}
                                </span>
                              ) : (
                                status.charAt(0).toUpperCase() + status.slice(1)
                              )}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-200 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Status updates are saved automatically
            </div>
            <Button
              data-testid="save-attendance-btn"
              onClick={submitAttendance}
              className="bg-[#1E624A] hover:bg-[#164935]"
            >
              Final Save & Submit
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl p-12 text-center text-gray-500">
          No students in this class
        </div>
      )}
    </div>
  );
};

export default Attendance;
